import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../ui/Badge';
import { cn } from '@hero/web-lib';
import type { PartyRole } from '@hero/schema';

const ROLE_CLASSES: Partial<Record<PartyRole, string>> = {
  business_customer: 'bg-primary-100 text-logoBlue border-transparent',
  individual_customer: 'bg-gray-100 text-gray-600 border-transparent',
  carrier_company: 'bg-emerald-100 text-emerald-700 border-transparent',
  broker: 'bg-amber-100 text-amber-700 border-transparent',
};

const FALLBACK_CLASS = 'bg-gray-100 text-gray-600 border-transparent';

export interface ClientRolesBadgeProps {
  roles: PartyRole[];
  className?: string;
  /**
   * Maximum number of roles to render before collapsing the rest into a
   * "+N" pill. Defaults to 2 to keep table cells tidy.
   */
  max?: number;
}

/**
 * Compact badge stack for `Client.roles[]` (multi-valued, network
 * model). Replaces the legacy single-value `CustomerTypeBadge`.
 *
 * Renders up to `max` role badges and collapses the overflow.
 */
export function ClientRolesBadge({ roles, className, max = 2 }: ClientRolesBadgeProps) {
  const { t } = useTranslation(['m-client']);
  if (!roles?.length) return null;

  const visible = roles.slice(0, max);
  const overflow = roles.length - visible.length;

  return (
    <div className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {visible.map(role => (
        <Badge
          key={role}
          variant="outline"
          className={cn(ROLE_CLASSES[role] ?? FALLBACK_CLASS)}>
          {t(`m-client:roles.${role}.title`)}
        </Badge>
      ))}
      {overflow > 0 && (
        <Badge variant="outline" className={FALLBACK_CLASS}>
          +{overflow}
        </Badge>
      )}
    </div>
  );
}
